import React from "react";
import {
  ShoppingCart,
  Shield,
  TruckIcon,
  CreditCard,
  Lock,
} from "lucide-react";
import type { CartSummaryProps } from "./types";

const CartSummary: React.FC<CartSummaryProps> = ({
  cartItems,
  itemsTotal,
  itemsDiscount,
  subtotal,
  shippingCost,
  estimatedTotal,
  onCheckout,
  isLoading,
}) => {
  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className="bg-neutral-900 rounded-2xl shadow-xl border border-neutral-800 overflow-hidden sticky top-4">
      {/* Header */}
      <div className="bg-primary p-6">
        <h3 className="text-xl font-bold flex items-center gap-2 text-black">
          <ShoppingCart className="w-5 h-5" />
          Order Summary
        </h3>
        <p className="text-sm text-black/70 mt-1">
          {cartItems.length} {cartItems.length === 1 ? "item" : "items"} in your
          cart
        </p>
      </div>

      <div className="p-6">
        {/* Item Preview */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {cartItems.slice(0, 3).map((item) => (
            <div key={item.id} className="relative shrink-0">
              <img
                src={item.image || "/api/placeholder/60/60"}
                alt={item.name}
                className="w-16 h-16 object-cover rounded-lg border border-neutral-700 shadow-sm"
              />
            </div>
          ))}
          {cartItems.length > 3 && (
            <div className="w-16 h-16 bg-neutral-800 rounded-lg border border-neutral-700 flex items-center justify-center text-xs text-neutral-300 font-bold shadow-sm">
              +{cartItems.length - 3}
            </div>
          )}
        </div>

        {/* Price Breakdown */}
        <div className="space-y-4 mb-6 pb-6 border-b border-neutral-800">
          <div className="flex justify-between text-sm text-neutral-400">
            <span>Items total</span>
            <span className="line-through">{formatCurrency(itemsTotal)}</span>
          </div>

          {itemsDiscount > 0 && (
            <div className="flex justify-between text-sm bg-green-500/10 -mx-2 px-2 py-2 rounded-lg">
              <span className="text-green-400 font-medium">Items discount</span>
              <span className="text-green-400 font-bold">
                -{formatCurrency(itemsDiscount)}
              </span>
            </div>
          )}

          <div className="flex justify-between font-semibold text-white">
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>

          <div className="flex justify-between text-sm text-neutral-400">
            <span className="flex items-center gap-1">
              <TruckIcon className="w-4 h-4" />
              Shipping
            </span>
            <span
              className={
                shippingCost === 0 ? "text-green-400 font-semibold" : ""
              }
            >
              {shippingCost === 0 ? "FREE" : formatCurrency(shippingCost)}
            </span>
          </div>
        </div>

        {/* Total */}
        <div className="bg-neutral-800 -mx-6 px-6 py-6 mb-6 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-white">Total</span>
            <div className="text-right">
              <div className="text-2xl font-extrabold text-primary">
                {formatCurrency(estimatedTotal)}
              </div>
            </div>
          </div>
        </div>

        {/* Checkout Button */}
        <button
          onClick={onCheckout}
          disabled={isLoading || cartItems.length === 0}
          className="
            w-full bg-primary hover:bg-primary-dark
            text-black py-4 rounded-xl font-bold text-lg
            transition-all duration-300 ease-out
            shadow-lg hover:shadow-xl hover:shadow-primary/30
            transform hover:-translate-y-1
            active:scale-[0.98] active:translate-y-0
            disabled:opacity-50 disabled:cursor-not-allowed 
            disabled:hover:transform-none disabled:hover:shadow-lg
            flex items-center justify-center gap-3
            relative overflow-hidden
            group
          "
        >
          <span className="relative z-10 flex items-center gap-3">
            <ShoppingCart className="w-6 h-6 group-hover:scale-110 transition-transform" />
            <span className="tracking-wide">
              {isLoading ? "Processing..." : "Proceed to Checkout"}
            </span>
          </span>

          {/* Shimmer effect */}
          <div className="absolute inset-0 bg-linear-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 transition-all duration-700 group-hover:translate-x-full"></div>
        </button>

        {/* Trust Badges */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-neutral-800 rounded-lg">
            <Shield className="w-5 h-5 text-green-400 mx-auto mb-1" />
            <span className="text-xs text-neutral-400 font-medium block">
              Secure
            </span>
          </div>
          <div className="text-center p-3 bg-neutral-800 rounded-lg">
            <Lock className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <span className="text-xs text-neutral-400 font-medium block">
              Encrypted
            </span>
          </div>
          <div className="text-center p-3 bg-neutral-800 rounded-lg">
            <CreditCard className="w-5 h-5 text-purple-400 mx-auto mb-1" />
            <span className="text-xs text-neutral-400 font-medium block">
              Safe Pay
            </span>
          </div>
        </div>

        {/* Buyer Protection */}
        <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
          <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-white">
            <Shield className="w-4 h-4 text-green-400" />
            100% Buyer Protection
          </h4>
          <p className="text-xs text-neutral-400 leading-relaxed">
            Full refund if the item is not as described or not delivered. Your
            purchase is protected.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CartSummary;
